import React, { FC, ChangeEvent } from 'react';
import { Input, Checkbox, InputNumber } from 'antd';
import { CreateAnnotationProperty } from '../AnnotationForm.types';
import { valueUpdated } from '../Helpers';
import { PropertyType } from '../../../../../shared/models/propertyType.enum';
import { FindingTypeProperty } from '../../../AnnotationStandardForm/AnnotationStandardForm.slice';
import {
  restrictInputFor2Decimals,
  restrictInputForDigitsAndSeparators,
  prepareIntValue,
  prepareDoubleValue,
  ThousandSeparatorDotRegex,
  CommaSeparatorToDotRegex
} from '../Utils';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import styles from '../AnnotationForm.module.scss';

type Props = {
  property: CreateAnnotationProperty;
  propertyTemplate: FindingTypeProperty;
  properties: CreateAnnotationProperty[][];
  updateProperties: (properties: CreateAnnotationProperty[][]) => void;
};

const PropertyInputType: FC<Props> = ({ property, propertyTemplate, properties, updateProperties }) => {
  const { findingTypePropertyId, value, isReadonly } = property;

  const onChangeIntValue = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event!.target.value.toString().replace(ThousandSeparatorDotRegex, '');
    valueUpdated(findingTypePropertyId, restrictInputForDigitsAndSeparators(event!.target.value) ? newValue : value, updateProperties, properties);
  };

  const onChangeDoubleValue = (value: number | string | any) => {
    valueUpdated(findingTypePropertyId, value.toString(), updateProperties, properties);
  };

  const onChangeBooleanValue = (event: CheckboxChangeEvent) => {
    valueUpdated(findingTypePropertyId, event.target.value, updateProperties, properties);
  };

  const onChangeStringValue = (event: ChangeEvent<HTMLInputElement>) => {
    valueUpdated(findingTypePropertyId, event.target.value, updateProperties, properties);
  };

  const IntegerInputProperty = (
    <Input
      className={styles.inputStyles}
      placeholder='0'
      value={prepareIntValue(value)}
      onChange={onChangeIntValue}
      readOnly={isReadonly}
      disabled={isReadonly}
    />
  );

  const DoubleInputProperty = (
    <InputNumber
      className={styles.inputStyles}
      placeholder='0,00'
      precision={2}
      value={prepareDoubleValue(value)}
      onChange={onChangeDoubleValue}
      readOnly={isReadonly}
      disabled={isReadonly}
    />
  );

  const BooleanInputProperty = (
    <Checkbox className={styles.inputStyles} onChange={onChangeBooleanValue} checked={value === 'true'} disabled={isReadonly}></Checkbox>
  );

  const StringInputProperty = (
    <Input
      className={styles.inputStyles}
      placeholder='Enter Value'
      value={value}
      onChange={onChangeStringValue}
      readOnly={isReadonly}
      disabled={isReadonly}
    />
  );

  const Inputs = new Map<number, JSX.Element>([
    [0, IntegerInputProperty],
    [1, DoubleInputProperty],
    [2, BooleanInputProperty],
    [3, StringInputProperty]
  ]);

  const getInput = (type: PropertyType) => {
    if (Inputs.has(type)) {
      return Inputs.get(type)!;
    }

    return StringInputProperty;
  };

  return getInput(propertyTemplate.type);
};

export default PropertyInputType;
